import { logger } from '../lib/logger';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, LogIn, MailCheck, KeyRound, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Estado do 2FA (etapa 2)
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [challengeId, setChallengeId] = useState(null);
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(0);   // expiração do código
  const [resendIn, setResendIn] = useState(0);      // cooldown de reenvio
  const otpInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  useEffect(() => {
    if (step === 'otp' && otpInputRef.current) otpInputRef.current.focus();
  }, [step]);

  const parseErro = (error, fallback) => {
    const status = error.response?.status;
    if (status === 429) return error.response?.data?.detail || 'Muitas tentativas. Aguarde alguns instantes.';
    return error.response?.data?.detail || fallback;
  };

  // Etapa 1: usuário + senha -> dispara código por e-mail
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        username: formData.username,
        password: formData.password,
      }, { withCredentials: true });

      if (data.requires_2fa) {
        setChallengeId(data.challenge_id);
        setStep('otp');
        setCode('');
        setCountdown(data.expires_in || 120);
        setResendIn(30);
        toast({ title: 'Código enviado!', description: 'Enviamos um código de verificação para o e-mail do administrador.' });
      }
    } catch (error) {
      logger.error('Erro no login:', error);
      toast({ title: 'Erro no login', description: parseErro(error, 'Usuário ou senha incorretos.'), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  // Etapa 2: confirma o código -> emite sessão
  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/login/verify`, {
        challenge_id: challengeId,
        code: code.trim(),
      }, { withCredentials: true });

      localStorage.removeItem('admin_token');
      toast({ title: 'Login realizado com sucesso!', description: 'Bem-vindo ao painel administrativo.' });
      navigate('/admin/dashboard');
    } catch (error) {
      logger.error('Erro na verificação 2FA:', error);
      const status = error.response?.status;
      toast({ title: 'Verificação falhou', description: parseErro(error, 'Código incorreto.'), variant: 'destructive' });
      // Se o desafio expirou/foi invalidado, volta para a etapa de credenciais
      if (status === 400 || status === 429) {
        setStep('credentials');
        setChallengeId(null);
        setCode('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || !challengeId) return;
    setIsLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/login/resend`, {
        challenge_id: challengeId,
      }, { withCredentials: true });
      setCountdown(data.expires_in || 120);
      setResendIn(30);
      setCode('');
      toast({ title: 'Novo código enviado!', description: 'Verifique o e-mail do administrador.' });
    } catch (error) {
      toast({ title: 'Não foi possível reenviar', description: parseErro(error, 'Tente novamente.'), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500 rounded-full mb-4">
            <Shield className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">WING ACADEMY</h1>
          <p className="text-gray-400">Painel Administrativo</p>
        </div>

        <div className="glass-dark p-8 rounded-2xl border border-yellow-500/20">
          {step === 'credentials' ? (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-credentials-form">
              <div>
                <label className="block text-white font-medium mb-2">Usuário</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors duration-300"
                  placeholder="Digite seu usuário"
                  data-testid="admin-username-input"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Senha</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors duration-300"
                  placeholder="Digite sua senha"
                  data-testid="admin-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-bold transition-all duration-300 hover:scale-105 glow-yellow flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-login-btn"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    Enviando código...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    ENTRAR
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-yellow-400" />
                Protegido por verificação em duas etapas
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-6" data-testid="admin-otp-form">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-500/15 rounded-full mb-3">
                  <MailCheck className="w-7 h-7 text-yellow-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Verificação em duas etapas</h2>
                <p className="text-gray-400 text-sm mt-2">
                  Enviamos um código de 6 dígitos para o e-mail do administrador. Digite-o abaixo para continuar.
                </p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2 flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-yellow-400" /> Código de verificação
                </label>
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-4 text-white text-center text-3xl font-bold tracking-[0.5em] placeholder-gray-600 focus:outline-none focus:border-yellow-500 transition-colors duration-300"
                  placeholder="000000"
                  data-testid="admin-otp-input"
                />
                <div className="mt-2 text-center text-sm" data-testid="admin-otp-countdown">
                  {countdown > 0 ? (
                    <span className="text-gray-400">Código válido por <strong className="text-yellow-400">{fmt(countdown)}</strong></span>
                  ) : (
                    <span className="text-red-400">Código expirado. Reenvie para receber um novo.</span>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || code.length !== 6}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-bold transition-all duration-300 hover:scale-105 glow-yellow flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-otp-verify-btn"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    Verificando...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    CONFIRMAR CÓDIGO
                  </>
                )}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setStep('credentials'); setChallengeId(null); setCode(''); }}
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5"
                  data-testid="admin-otp-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Voltar
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendIn > 0 || isLoading}
                  className="text-yellow-400 hover:text-yellow-300 transition-colors disabled:text-gray-600 disabled:cursor-not-allowed font-medium"
                  data-testid="admin-otp-resend-btn"
                >
                  {resendIn > 0 ? `Reenviar em ${resendIn}s` : 'Reenviar código'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Voltar */}
        <div className="text-center mt-6">
          <a href="/" className="text-gray-400 hover:text-yellow-400 transition-colors duration-300 text-sm">
            ← Voltar para o site
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
